
import React from 'react';
import { motion } from 'framer-motion';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Control } from 'react-hook-form';
import { SizeOption } from './types';

interface SizeButtonsProps {
  gender: 'male' | 'female';
  sizes: SizeOption[];
  control: Control<any>;
  onHoverSize: (size: string | null) => void;
  getButtonColors: (isSelected: boolean) => string;
}

const SizeButtons: React.FC<SizeButtonsProps> = ({ 
  gender, 
  sizes, 
  control, 
  onHoverSize, 
  getButtonColors 
}) => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.05,
        duration: 0.3
      } 
    }
  };

  const itemVariants = {
    hidden: { y: 10, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  // Add gender prefix to size value - Use Nam_/Nu_ prefix format
  const getGenderedSizeValue = (sizeValue: string) => {
    return gender === 'male' ? `Nam_${sizeValue}` : `Nu_${sizeValue}`;
  };
  
  // Remove gender prefix from size value
  const removeGenderPrefix = (sizeValue: string | undefined) => {
    if (!sizeValue) return '';
    
    if (sizeValue.startsWith('Nam_')) return sizeValue.substring(4);
    if (sizeValue.startsWith('Nu_')) return sizeValue.substring(3);
    return sizeValue;
  };

  return (
    <FormField
      control={control}
      name="t_shirt_size"
      render={({ field }) => (
        <FormItem className="relative z-10">
          <FormLabel className={`text-sm font-medium ${gender === 'male' ? 'text-blue-700' : 'text-pink-700'} flex items-center`}>
            <span>Kích thước áo {gender === 'male' ? 'Nam' : 'Nữ'}</span>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <HelpCircle className={`h-4 w-4 ml-1 ${gender === 'male' ? 'text-blue-600' : 'text-pink-600'}`} />
                </TooltipTrigger>
                <TooltipContent className={`bg-white p-3 shadow-lg border ${gender === 'male' ? 'border-blue-300' : 'border-pink-300'}`}>
                  <p className="text-sm">Đo ngang ngực (rộng áo) và chiều dài từ vai xuống (dài áo)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </FormLabel>
          <FormControl>
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <ToggleGroup
                type="single"
                value={removeGenderPrefix(field.value)}
                onValueChange={(value) => {
                  if (value) {
                    // Always save with the current gender prefix
                    const genderedValue = getGenderedSizeValue(value);
                    field.onChange(genderedValue);
                  }
                }}
                className="flex flex-wrap justify-center gap-2"
              >
                {sizes.map((size) => {
                  // Check if current size is selected - handle both prefixed and unprefixed values
                  const genderedValue = getGenderedSizeValue(size.value);
                  const isSelected = field.value === genderedValue || 
                    (gender === 'male' && field.value?.startsWith('Nam_') && field.value.substring(4) === size.value) || 
                    (gender === 'female' && field.value?.startsWith('Nu_') && field.value.substring(3) === size.value);
                  
                  return (
                    <motion.div key={size.value} variants={itemVariants}>
                      <ToggleGroupItem
                        value={size.value} // Original size value for the toggle
                        className={`
                          px-4 py-3 rounded-md border-2 transition-all duration-200 ease-in-out
                          ${isSelected 
                            ? `${getButtonColors(true)} text-white !font-semibold`
                            : getButtonColors(false)
                          }
                          min-w-16 relative
                          ${isSelected ? 'transform scale-110 shadow-xl' : ''}
                        `}
                        onMouseEnter={() => onHoverSize(size.value)}
                        onMouseLeave={() => onHoverSize(null)}
                      >
                        {size.label}
                      </ToggleGroupItem>
                    </motion.div>
                  );
                })}
              </ToggleGroup>
            </motion.div>
          </FormControl>
          <FormMessage className="text-xs text-red-500" />
        </FormItem>
      )}
    />
  );
};

export default SizeButtons;
